import {Controller} from "@hotwired/stimulus"
import { makeSendData } from "./lib/nvcat";

// Connects to data-controller="pos"
export default class extends Controller {
    static targets = [
        "input",
        "recvData",
        "scrollable",
        "epcSelect",
        "summaryCount",
        "summaryTotalPrice",
        "summaryCashPayment",
        "summaryChangeAmount",
        "summaryRealTotalPrice",
        "summaryPrice",
        "promotion",
        "summaryPromotionName",
        "summaryPromotionDiscount",
        "summaryDutyFree",
        "summaryCardVat",
        "summaryCashVat",
        "summaryVoucherVat",
        "totalCount",
        "totalPrice",
        "items",
        "totalSummary",
        "alertWindow",
        "halbu",
        "submitButton",
        "cashButton",
        "confirmButton",
        "cashPurchase"
    ]
    static values = {
        halbuCount: Number,
        promotions: String,
        rawData: Array,
        initialCart: Object
    }

    serverError = "서버통신 오류입니다.<br>관리자 에게 문의 해주세요."

    initialize() {
        this.barcodeInput = ''
        this.lastKeyTime = Date.now()
    }

    connect() {
        this.posChannel = new BroadcastChannel("pos-channel")
        this.posChannel.onmessage = (event) => {
            const { type, url } = event.data || {}
            if (type === "repack" && url) {
                // 부모창이 스스로 이동(=새로고침 포함)
                window.location.href = url
            }
        }

        this.selectedEpcs = {}

        const raw = this.hasRawDataValue ? this.rawDataValue : null
        this.items = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : [])

        if (this.hasInitialCartValue) {
            const initial = this.initialCartValue
            if (initial && initial.total_summary) {
                this.updateUIElements(
                    this.items,
                    initial.promotion_visible,
                    initial.submit_button_enabled,
                    initial.cash_button_enabled,
                    initial.confirm_button_enabled,
                    initial.total_summary
                )
            }
        }

        this.boundHandleKeydown = this.handleKeydown.bind(this)
        document.addEventListener("keydown", this.boundHandleKeydown)

        this.boundCartUpdated = (event) => {
            const cartResult = event.detail
            if (Array.isArray(cartResult.items)) this.items = cartResult.items
            this.updateUIElements(
                this.items,
                cartResult.promotion_visible,
                cartResult.submit_button_enabled,
                cartResult.cash_button_enabled,
                cartResult.confirm_button_enabled,
                cartResult.total_summary
            )
        }
        window.addEventListener("pos:cart-updated", this.boundCartUpdated)

        this.countdownInterval = null
        this.timeoutId = null

        this.boundClearTimers = this.clearTimers.bind(this)
        window.addEventListener("modals:timer:clear", this.boundClearTimers)
    }

    disconnect() {
        if (this.posChannel) this.posChannel.close()
        document.removeEventListener('keydown', this.boundHandleKeydown)
        window.removeEventListener("modals:timer:clear", this.boundClearTimers)
        window.removeEventListener("pos:cart-updated", this.boundCartUpdated)
        this.clearTimers()
    }

    getControllerType() {
        const url = window.location.pathname.split("/")
        return url[1] || null
    }

    getPosId() {
        const match = window.location.pathname.match(/\/pos\/(\d+)\/(sale|kiosk)/)
        return match ? match[1] : null
    }

    async cartAction(actionName, payload = {}) {
        const posId = this.getPosId()
        if (!posId) return

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const url = `/pos/${posId}/${actionName}`

        try {
            const response = await fetch(url, {
                method:  "POST",
                headers: {
                    "X-CSRF-Token": csrfToken,
                    "Content-Type": "application/json",
                    "Accept":       "text/vnd.turbo-stream.html, application/json"
                },
                body: JSON.stringify(payload)
            })

            // mutator 가 JSON 으로 오류(중복 상품 등)를 반환한 경우
            const contentType = response.headers.get("Content-Type") || ""
            if (contentType.includes("application/json")) {
                const data = await response.json()
                if (data.status !== "ok" && data.message) {
                    this.openModal(data.message)
                }
                return
            }

            if (!response.ok) throw new Error(this.serverError)

            // Turbo Stream 을 body 에 append → Turbo 가 order_info_list 갱신
            const turboStream = await response.text()
            document.body.insertAdjacentHTML("beforeend", turboStream)

            // 서버가 헤더로 전달한 summary 로 기존 updateUIElements 를 그대로 호출
            const summaryHeader = response.headers.get("X-Cart-Summary")
            if (summaryHeader) {
                const cartResult = JSON.parse(summaryHeader)
                if (Array.isArray(cartResult.items)) this.items = cartResult.items
                this.updateUIElements(
                    this.items,
                    cartResult.promotion_visible,
                    cartResult.submit_button_enabled,
                    cartResult.cash_button_enabled,
                    cartResult.confirm_button_enabled,
                    cartResult.total_summary
                )
                if (cartResult.bonus_message) {
                    this.openModal(cartResult.bonus_message, "notice")
                }
            }

            if (this.hasScrollableTarget) this.scrollToBottom()
        } catch (error) {
            console.error("cartAction failed:", error)
            this.openModal(this.serverError)
        }
    }

    scrollToBottom() {
        const element = this.scrollableTarget
        setTimeout(() => {
            element.scrollTop = element.scrollHeight
        }, 100)
    }

    scrollToUpdatedRow(index) {
        const itemIndex = document.getElementById(`item-${index}`)
        if (itemIndex) {
            itemIndex.classList.add("bg-warning/10")
            setTimeout(() => {
                itemIndex.classList.remove("bg-warning/10")
            }, 300)
            itemIndex.scrollIntoView()
        }
    }

    handleKeydown(event) {
        if (!window.location.pathname.includes("/pos/")) return

        const currentTime = Date.now()

        // 바코드 스캔의 입력 간격이 짧은 경우
        if (currentTime - this.lastKeyTime > 100) {
            this.barcodeInput = '' // 새 바코드로 간주
        }

        this.lastKeyTime = currentTime

        const modalsFrame = document.querySelector('turbo-frame[id="modals"]')
        if (modalsFrame && modalsFrame.innerHTML.trim() !== "") return
        if (modalsFrame && !modalsFrame.contains(document.activeElement)) modalsFrame.innerHTML = ""

        // 엔터키가 눌리면 바코드를 서버로 전송
        if (event.key === 'Enter') {
            if (this.barcodeInput.length > 0) {
                let convertedBarcodeInput
                convertedBarcodeInput = this.barcodeInput
                    .replace(/ㅁ/g, "A")
                    .replace(/ㅠ/g, "B")
                    .replace(/ㅊ/g, "C")
                    .replace(/ㅇ/g, "D")
                    .replace(/ㄷ/g, "E")
                    .replace(/ㄸ/g, "E")
                    .replace(/ㄹ/g, "F")
                    .replace(/ㅎ/g, "G")
                    .replace(/ㅗ/g, "H")
                    .replace(/ㅑ/g, "I")
                    .replace(/ㅓ/g, "J")
                    .replace(/ㅏ/g, "K")
                    .replace(/ㅣ/g, "L")
                    .replace(/ㅡ/g, "M")
                    .replace(/ㅜ/g, "N")
                    .replace(/ㅐ/g, "O")
                    .replace(/ㅔ/g, "P")
                    .replace(/ㅂ/g, "Q")
                    .replace(/ㄱ/g, "R")
                    .replace(/ㄴ/g, "S")
                    .replace(/ㅅ/g, "T")
                    .replace(/ㅕ/g, "U")
                    .replace(/ㅍ/g, "V")
                    .replace(/ㅈ/g, "W")
                    .replace(/ㅌ/g, "X")
                    .replace(/ㅛ/g, "Y")
                    .replace(/ㅋ/g, "z")

                this.updateItemsList(event, convertedBarcodeInput)
                this.barcodeInput = '' // 입력 초기화
            }
        } else if (event.key === 'Shift') {
            this.barcodeInput += ''
        } else if (event.key === 'CapsLock') {
            this.barcodeInput += ''
        } else if (event.key === 'Unidentified') {
            this.barcodeInput += ''
        } else {
            this.barcodeInput += event.key
            console.log(this.barcodeInput)
        }
    }

    updateItemsList(event, barcodeInput = null) {
        event.preventDefault()
        const selectedEpc = barcodeInput || this.epcSelectTarget.value
        this.cartAction("cart_add_by_code", { code: selectedEpc })
    }

    updateRepotCount(event) {
        event.preventDefault()
        const button = event.currentTarget
        const calcType = button.dataset.calcType
        const orderInfoId = button.dataset.orderInfoId

        this.cartAction("cart_set_repot_count", {
            order_info_id: orderInfoId,
            calc_type:     calcType
        })
    }

    openModal(message, textClass = "danger") {
        const url = `/modals/open?type=alert&close_button=false&message=${message}&text_class=${textClass}`

        fetch(url, {
            method: "POST", headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
            }, body: JSON.stringify({turbo_frame: "modals"})
        })
            .then(response => {
                if (!response.ok) throw new Error("네트워크 오류")
                return response.text()
            })
            .then(html => {
                this.clearTimers()

                const modalsFrame = document.querySelector('turbo-frame[id="modals"]')
                if (modalsFrame) {
                    modalsFrame.innerHTML = html

                    // 카운트다운 초기화
                    let countdown = 3
                    const countdownElement = document.getElementById("close-countdown")
                    if (countdownElement) {
                        this.countdownInterval = setInterval(() => {
                            countdown -= 1
                            if (countdown > 0) {
                                countdownElement.textContent = countdown
                            } else {
                                clearInterval(this.countdownInterval)
                                this.countdownInterval = null
                            }
                        }, 1000)
                        this.timeoutId = setTimeout(() => {
                            modalsFrame.innerHTML = ""
                            this.timeoutId = null
                        }, 3000)

                    }
                }
            })
            .catch(error => console.error("에러:", error))
    }

    clearTimers() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
        }
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
    }

    // UI 요소들을 업데이트하는 헬퍼 메서드
    updateUIElements(items, promotionVisible, submitButtonEnabled, cashButtonEnabled, confirmButtonEnabled, totalSummary) {
        // 기본 요소들 업데이트
        if (this.hasSummaryCountTarget) {
            this.summaryCountTarget.textContent = totalSummary.total_count.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryTotalPriceTarget) {
            const vat  = totalSummary.total_vat_price || 0
            const duty = totalSummary.total_duty_free_price || 0
            const voucher = totalSummary.total_voucher_price || 0

            this.summaryTotalPriceTarget.textContent = (vat + duty - voucher).toLocaleString("ko-KR")
        }
        if (this.hasSummaryPriceTarget) {
            this.summaryPriceTarget.textContent = totalSummary.total_price.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryDutyFreeTarget) {
            this.summaryDutyFreeTarget.textContent = totalSummary.total_duty_free_price.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryCardVatTarget) {
            this.summaryCardVatTarget.textContent = totalSummary.card_vat.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryCashVatTarget) {
            this.summaryCashVatTarget.textContent = totalSummary.cash_vat.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryVoucherVatTarget) {
            this.summaryVoucherVatTarget.textContent = totalSummary.voucher_vat.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryCashPaymentTarget) {
            if (totalSummary.total_cash_price > 0) {
                this.summaryCashPaymentTarget.textContent = totalSummary.total_cash_price.toLocaleString("ko-KR") || "0"
            } else {
                this.summaryCashPaymentTarget.textContent = "0"
            }
        }
        if (this.hasSummaryChangeAmountTarget) {
            this.summaryChangeAmountTarget.textContent = totalSummary.change_amount.toLocaleString("ko-KR") || "0"
        }
        if (this.hasSummaryRealTotalPriceTarget) {
            const totalPrice = totalSummary.total_price || 0
            const cashPrice = totalSummary.total_cash_price || 0
            const change = totalSummary.change_amount || 0
            const real = totalPrice + cashPrice - change
            this.summaryRealTotalPriceTarget.textContent = real.toLocaleString("ko-KR")
        }
        // 프로모션 표시 제어
        if (this.hasPromotionTarget) {
            if (promotionVisible) {
                this.promotionTarget.classList.remove("hidden")
                if (this.summaryPromotionNameTarget) {
                    this.summaryPromotionNameTarget.textContent = totalSummary.promotion_name || ""
                }
                if (this.summaryPromotionDiscountTarget) {
                    this.summaryPromotionDiscountTarget.textContent = `-${totalSummary.promotion_discount.toLocaleString("ko-KR")}` || "0"
                }
            } else {
                this.promotionTarget.classList.add("hidden")
            }
        }

        // 결제 버튼 상태 업데이트
        if (this.hasSubmitButtonTarget) {
            if (submitButtonEnabled) {
                this.submitButtonTarget.classList.remove("disabled")
                this.submitButtonTarget.disabled = false
            } else {
                this.submitButtonTarget.classList.add("disabled")
                this.submitButtonTarget.disabled = true
            }
        }

        // 현금결제 버튼 상태 업데이트
        if (this.hasCashButtonTarget) {
            if (cashButtonEnabled) {
                this.cashButtonTarget.classList.remove("disabled")
                this.cashButtonTarget.disabled = false
            } else {
                this.cashButtonTarget.classList.add("disabled")
                this.cashButtonTarget.disabled = true
            }
        }

        // 결제 확인 버튼 상태 업데이트
        if (this.hasConfirmButtonTarget) {
            if (confirmButtonEnabled) {
                this.confirmButtonTarget.classList.remove("disabled")
                this.confirmButtonTarget.disabled = false
            } else {
                this.confirmButtonTarget.classList.add("disabled")
                this.confirmButtonTarget.disabled = true
            }
        }

        // Hidden form fields 업데이트
        if (this.itemsTargets) {
            this.itemsTargets.forEach((data) => {
                data.value = JSON.stringify(items)
            })
        }

        if (this.totalCountTargets) {
            this.totalCountTargets.forEach((totalCount) => {
                totalCount.value = totalSummary.total_count.toLocaleString("ko-KR") || "0"
            })
        }
        if (this.totalPriceTargets) {
            this.totalPriceTargets.forEach((totalPrice) => {
                totalPrice.value = totalSummary.total_price.toLocaleString("ko-KR") || "0"
            })
        }
        if (this.totalSummaryTargets) {
            this.totalSummaryTargets.forEach((totalSum) => {
                totalSum.value = JSON.stringify(totalSummary) || {}
            })
        }
        if (this.cashPurchaseTargets) {
            this.cashPurchaseTargets.forEach((el) => {
                el.value = totalSummary.total_cash_price || 0
            })
        }
    }

    clickItem(event) {
        const itemEditor = document.querySelector('[data-controller~="item-editor"]')
        if (itemEditor?.dataset.editing === 'true') return

        const itemData = JSON.parse(event.currentTarget.dataset.posItemParam)
        const optionsUrl = event.currentTarget.dataset.optionsUrl
        if (optionsUrl) {
            this.openOptionsModal(optionsUrl)
            return
        }
        this.cartAction("cart_add_item", { pos_item_id: itemData.id })
    }

    openOptionsModal(url) {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        const form = document.createElement("form")
        form.method = "post"
        form.action = url
        form.dataset.turboFrame = "modals"
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "authenticity_token"
        input.value = csrf
        form.appendChild(input)
        document.body.appendChild(form)
        form.requestSubmit()
        form.remove()
    }

    removeItem(event) {
        event.preventDefault()
        const button = event.currentTarget
        const orderInfoId = button.dataset.orderInfoId
        const voucherPrice = button.dataset.voucherPrice
        const code = button.dataset.code

        this.cartAction("cart_remove_item", {
            order_info_id: orderInfoId,
            voucher_price: voucherPrice,
            code:          code
        })
    }

    // 카드 부분 끝

    // 결제 부분 시작
    halbu(event) {
        event.preventDefault()
        if (this.halbuCountValue === 0) this.halbuCountValue = 1
        let halbuElement = this.halbuTarget
        const type = event.currentTarget.dataset.type

        if (type === "minus") {
            if (halbuElement.textContent === "일시불") {
                return
            } else {
                if (this.halbuCountValue === 2) {
                    this.halbuCountValue -= 1
                    halbuElement.textContent = "일시불"
                } else {
                    this.halbuCountValue -= 1
                    halbuElement.textContent = `${this.halbuCountValue}개월`
                }
            }
        } else if (type === "plus") {
            this.halbuCountValue += 1
            halbuElement.textContent = `${this.halbuCountValue}개월`
        }
        let halbuInputElement = document.getElementById("halbu-input")
        halbuInputElement.value = this.halbuCountValue.toString().padStart(2, '0')
    }

    isSubmitting = false

    // kiosk 리팩토링 누락분: 결제 버튼 클릭 시 pending 업데이트 완료 후 submit 허용
    syncBeforeSubmit(_event) {
        // pos_controller 는 debounce 없이 서버 응답 헤더로 즉시 동기화하므로
        // 별도 처리 없이 기본 form submit 을 그대로 통과시킨다.
    }

    formSubmit(event) {
        event.preventDefault()

        const btn = event.submitter || this.element.querySelector('button[type="submit"]')
        const originalHTML = btn.innerHTML
        btn.disabled = true
        btn.innerHTML = "<i class='fa-solid fa-spinner fa-spin pe-4'></i>처리중..."

        // 중복 클릭 방지
        if (this.isSubmitting) {
            return
        }

        this.isSubmitting = true // 중복 방지 플래그 설정
        const sendData = this.formSendData()
        const preparedData = makeSendData(sendData)

        this.requestData(preparedData, this.element.dataset.itemType, null, null, this.element.dataset.voucherPriceId, btn, originalHTML).then()
    }

    formCancelSubmit(event) {
        event.preventDefault()

        // 중복 클릭 방지
        if (this.isSubmitting) {
            return
        }

        this.isSubmitting = true // 중복 방지 플래그 설정
        const sendData = this.formCancelSendData()
        const preparedData = makeSendData(sendData)

        const btn = event.submitter || this.element.querySelector('button[type="submit"]')
        const originalHTML = btn?.innerHTML
        if (btn) {
            btn.disabled = true
            btn.innerHTML = "<i class='fa-solid fa-spinner fa-spin pe-4'></i>처리중..."
        }

        this.requestData(preparedData, this.element.dataset.itemType, this.element.dataset.paymentId, this.element.dataset.cancel, null, btn, originalHTML).then()
    }

    formSendData() {
        const FS = "\x1C"
        const H7 = "\x07"

        // Fetch form values
        const money = this.element.querySelector("input[name='money']").value
        const tax = this.element.querySelector("input[name='tax']").value
        const bongsa = this.element.querySelector("input[name='bongsa']").value
        const halbu = this.element.querySelector("input[name='halbu']").value
        const myunse = this.element.querySelector("input[name='myunse']").value

        return ("NICEVCAT" + H7 + "0200" + FS + "10" + FS + "C" + FS + money + FS + tax + FS + bongsa + FS + halbu + FS + "" + FS + "" + FS + "" + FS + "" + FS + FS + FS + myunse + FS + FS + FS + FS + "" + FS + H7)
    }

    formCancelSendData() {
        const FS = "\x1C"
        const H7 = "\x07"

        const money = this.element.querySelector("input[name='money']").value
        const tax = this.element.querySelector("input[name='tax']").value
        const bongsa = this.element.querySelector("input[name='bongsa']").value
        const halbu = this.element.querySelector("input[name='halbu']").value
        const agreenum = this.element.querySelector("input[name='agreenum']").value
        const catid = this.element.querySelector("input[name='catid']").value
        const myunse = this.element.querySelector("input[name='myunse']").value
        const agreedate = this.element.querySelector("input[name='agreedate']").value

        return ("NICEVCAT" + H7 + "0420" + FS + "10" + FS + "C" + FS + money + FS + tax + FS + bongsa + FS + halbu + FS + agreenum + FS + agreedate + FS + catid + FS + FS + FS + FS + myunse + FS + FS + FS + FS + "" + FS + H7)
    }

    async requestData(sendData, itemType, paymentId, cancel, voucherPriceId, btn, originalHTML) {
        let nvcatResponseReceived = false
        try {
            // 테스트 끝나면 복구 해야할 부분 시작
            const response = await fetch("http://localhost:9188", {
                method: "POST", headers: {
                    "Content-Type": "text/plain",
                }, body: sendData,
            })

            const data = await response.text()
            nvcatResponseReceived = true
            this.recvDataTarget.value = data
            await this.logNvcatResponse(sendData, data, {
                source: "pos",
                httpStatus: response.status,
                httpOk: response.ok,
                responseType: data.length > 20 ? data.substring(16, 20) : "short",
                itemType: itemType,
                posId: new URLSearchParams(window.location.search).get("pos_id") || this.getPosId(),
                paymentId: paymentId,
                cancel: cancel,
                voucherPriceId: voucherPriceId
            })

            if (data.length > 20) {
                if ((data.substring(16, 20) === "0210")) {

                    const fields = data.split("\x1C");
                    const message = fields[7].replace(/\s+/g, " ").trim()

                    if (message.length > 4) {
                        await this.submitCheckoutResult(data, sendData, itemType, voucherPriceId)
                    } else {
                        this.openModal("결제 오류가 발생했습니다. 관리자에게 문의 해주세요.")
                    }

                } else if ((data.substring(16, 20) === "0430")) {
                    const fields = data.split("\x1C");
                    const message = fields[16].replace(/\s+/g, " ").trim()

                    if (message.includes("정상취소")) {
                        const csrfToken = document.querySelector('meta[name="csrf-token"]').content
                        const voucherCount = document.querySelector('input[name="voucher_count"]')?.value || 0

                        try {
                            const response = await fetch("/api/payment_cancel", {
                                method: "POST", headers: {
                                    "Content-Type": "application/json", "X-CSRF-Token": csrfToken,
                                }, body: JSON.stringify({
                                    agreenum: fields[7],
                                    voucher_count: voucherCount
                                }),
                            })

                            if (!response.ok) {
                                new Error(`HTTP error! status: ${response.status}`)
                            } else {
                                const ctrl = this.application.controllers.find(c => c.identifier === "receipt-printer")
                                ctrl.element.dataset.paymentId = paymentId
                                ctrl.element.dataset.cashOnly  = "false"
                                ctrl.element.dataset.cancel  = cancel
                                ctrl.printReceipt()
                            }
                        } catch (error) {
                            console.error("서버에서 오류가 발생했습니다.", error)
                            this.openModal("서버에서 오류가 발생했습니다. 관리자에게 문의 해주세요.")
                        }
                    }

                    this.openModal(message)
                } else {
                    this.openModal("결제 승인 오류 입니다.")
                }
            } else {
                this.openModal("서버 통신 오류 입니다.")
            }
            // 테스트 끝나면 복구 해야할 부분 끝

            // 테스트용 추후 삭제 필요 시작
            // const match = sendData.match(/\x1CC\x1C(\d+)\x1C/)
            // const data = `0520VCAT000005080210100000${match[1].toString().padStart(12, "0")}0000000000000000000000000016262667    24101516265006삼성마스타          06삼성카드            61895203       2393300001          IC카드 정상승인 16262667                                                                                       51883167********023933000011015162646NS241015162650D28050       IC                                                                            HPSH12208115770                             2891674426670         `
            // await this.submitCheckoutResult(data, sendData, itemType, voucherPriceId)
            // 테스트용 추후 삭제 필요 끝
        } catch (error) {
            if (!nvcatResponseReceived) {
                await this.logNvcatResponse(sendData, "", {
                    source: "pos",
                    httpStatus: "-",
                    httpOk: false,
                    responseType: "no_response",
                    itemType: itemType,
                    posId: new URLSearchParams(window.location.search).get("pos_id") || this.getPosId(),
                    paymentId: paymentId,
                    cancel: cancel,
                    voucherPriceId: voucherPriceId,
                    errorName: error?.name,
                    errorMessage: error?.message
                })
            }
            console.error("AJAX 통신 실패! NVCAT 실행 여부 확인!", error)
            this.openModal("AJAX 통신 실패! NVCAT 실행 여부 확인!")
        } finally {
            if (btn) {
                btn.disabled = false
                btn.innerHTML = originalHTML
            }
            this.isSubmitting = false
        }
    }

    async logNvcatResponse(sendData, data, context = {}) {
        try {
            await fetch("/api/nvcat_log", {
                method: "POST",
                keepalive: true,
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    source: context.source,
                    request: sendData,
                    response: data,
                    context: context
                })
            })
        } catch (error) {
            console.error("NVCAT 응답 로그 저장 실패", error)
        }
    }

    async submitCheckoutResult(data, request, itemType, voucherPriceId) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').content
        const voucherCount = this.element.dataset.voucherCount
        const items = this.element.dataset.items || null
        const totalSummary = this.element.dataset.totalSummary || null
        const saveListId = this.element.dataset.saveListId || null
        const single = this.element.dataset.single || false
        const posId = this.getPosId()

        try {
            const response = await fetch("/pos/checkout_result", {
                method: "POST", headers: {
                    "Content-Type": "application/json", "X-CSRF-Token": csrfToken,
                }, body: JSON.stringify({
                    pos_id: posId,
                    checkout_source: window.location.pathname.includes('/kiosk') ? "kiosk" : "pos",
                    data: data,
                    request: request,
                    item_type: itemType,
                    items: items,
                    total_summary: totalSummary,
                    voucher_count: voucherCount,
                    voucher_price_id: voucherPriceId,
                    save_list_id: saveListId,
                    single: single
                }),
            })

            if (!response.ok) {
                new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()
            const isKiosk = window.location.pathname.includes('/kiosk')
            const resultUrl = isKiosk
                ? `/pos/kiosk_receipt_print?payment_id=${result.payment_id}&pos_id=${posId}`
                : `/pos/receipt_print?payment_id=${result.payment_id}&pos_id=${posId}`
            window.location.href = resultUrl
        } catch (error) {
            console.error("서버에서 오류가 발생했습니다.", error)
            this.openModal("서버에서 오류가 발생했습니다. 관리자에게 문의 해주세요.")
        } finally {
            this.isSubmitting = false // 플래그 해제
        }
    }
}
